/**
 * Permissive email shape check used across forms, server actions, and API routes.
 *
 * Mirrors the historical inline regex `/^[^\s@]+@[^\s@]+\.[^\s@]+$/` but the
 * final segment excludes `.` (`[^\s@.]+`) so the two `[^\s@]+` runs no longer
 * overlap around the dot. The original could backtrack quadratically on hostile
 * input like `a@!.!.!.…` (polynomial ReDoS); this form is linear-time.
 *
 * The only behavior difference is strictly tighter: this rejects a domain ending
 * in a literal dot (e.g. `user@example.com.`) that the old pattern accepted. Such
 * addresses aren't valid emails, and this never accepts anything the old pattern
 * rejected (verified by fuzzing old vs new across millions of inputs).
 *
 * Not a deliverability or RFC-5322 validator — for that, send the email or use a
 * dedicated library. This catches obvious typos at form-submit time.
 */
export const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@.]+$/;

/**
 * Returns true when `value` looks like a syntactically valid email. Trims
 * whitespace before checking — callers don't need to normalize first.
 *
 * @param value - User-supplied email string (may include surrounding whitespace).
 * @returns Whether the trimmed string passes the permissive email regex.
 */
export function isValidEmail(value: string): boolean {
  return EMAIL_PATTERN.test(value.trim());
}

/**
 * Splits a comma-separated address string into trimmed, non-empty entries.
 *
 * @param value - Comma-separated address string (the storage shape used by `cc`-style fields).
 * @returns Trimmed, non-empty address entries.
 */
export function parseEmailList(value: string): string[] {
  return value
    .split(",")
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
}

/**
 * Matches any Unicode whitespace (including U+00A0 non-breaking space) plus the
 * zero-width characters (U+200B–U+200D, U+FEFF) that survive a plain `.trim()`.
 * Emails can't legally contain whitespace, so we strip these anywhere in the
 * string — this is what defeats "looks identical but doesn't match" values pasted
 * from spreadsheets (Sheets/Excel cells carry NBSP/zero-width chars).
 */
const INVISIBLE_WHITESPACE = /[\s\u00A0\u200B\u200C\u200D\uFEFF]/g;

/**
 * Domains whose account family shares ONE inbox — a message to any of them lands
 * in the same mailbox — so they must canonicalize to a single domain for matching.
 * Keyed by alias, valued by the canonical domain.
 *
 * - `googlemail.com` is Gmail's alternate domain (same inbox as `gmail.com`).
 * - `ymail.com` / `rocketmail.com` are legitimate Yahoo alias domains.
 *
 * This is domain-equivalence only; it says nothing about `+tag`/dot handling
 * (Yahoo, notably, is aliased here but is NOT plus-addressed — see below).
 */
const DOMAIN_ALIASES: Record<string, string> = {
  "googlemail.com": "gmail.com",
  "ymail.com": "yahoo.com",
  "rocketmail.com": "yahoo.com",
};

/**
 * Domains that treat `+tag` sub-addresses as one inbox (`a+x@d` === `a@d`), so the
 * tag is stripped for matching. Restricted on purpose: on a custom/corporate domain
 * a `+` is a literal, DISTINCT address that can belong to a different person, and
 * collapsing them would cross-link two people's private data. Explicitly excludes
 * Yahoo — Yahoo's "disposable addresses" are a separate opt-in mechanism, not `+tag`.
 *
 * `gmail.com` is handled by its own (stronger) branch below and isn't repeated here.
 */
const PLUS_ADDRESSING_DOMAINS = new Set([
  "outlook.com",
  "hotmail.com",
  "live.com",
  "msn.com",
  "icloud.com",
  "me.com",
  "mac.com",
  "fastmail.com",
  "protonmail.com",
  "proton.me",
]);

/**
 * Resolves a domain to its canonical form via {@link DOMAIN_ALIASES} (e.g.
 * `googlemail.com` -> `gmail.com`). Domain must already be lowercased/trimmed.
 * Exported so domain-only comparisons (SSO/allowlist matching) share these rules.
 *
 * @param domain - A bare, lowercased domain (no `@`, no path).
 * @returns The canonical domain, or the input unchanged when it has no alias.
 */
export function canonicalizeEmailDomain(domain: string): string {
  return DOMAIN_ALIASES[domain] ?? domain;
}

/**
 * The alias-canonicalized domain of an email (e.g. `googlemail.com` -> `gmail.com`),
 * or null when there's no domain. Pure/Edge-safe.
 *
 * @param email - Raw email (or null/undefined).
 * @returns The lowercased, alias-resolved domain, or null.
 */
export function canonicalEmailDomain(email: string | null | undefined): string | null {
  if (!email) return null;
  const cleaned = normalizeEmail(email);
  const at = cleaned.lastIndexOf("@");
  if (at <= 0) return null;
  return canonicalizeEmailDomain(cleaned.slice(at + 1));
}

/**
 * Whether a domain's matching is fully determined by the built-in string rules —
 * i.e. Gmail, the Yahoo family, or a known plus-addressing provider — so it needs
 * NO MX lookup. Callers use this to skip the mail-provider cache read for the
 * common consumer-email case. Domain must be alias-canonicalized already.
 *
 * @param domain - An alias-canonicalized, lowercased domain.
 * @returns true when built-in rules already cover it.
 */
export function hasBuiltInEmailRules(domain: string): boolean {
  return domain === "gmail.com" || domain === "yahoo.com" || PLUS_ADDRESSING_DOMAINS.has(domain);
}

/**
 * Normalizes an email for storage and display: strips surrounding + invisible /
 * zero-width whitespace (NBSP, ZWSP, BOM — the spreadsheet-paste characters a plain
 * `.trim()` misses) and lowercases. This is LOSSLESS with respect to the address —
 * it never drops dots or `+tag` — so it is safe to STORE or SEND to. For matching
 * two addresses to the same inbox, use {@link canonicalizeEmail} / {@link emailsMatch}.
 *
 * @param value - Raw email string.
 * @returns The whitespace-stripped, lowercased email.
 */
export function normalizeEmail(value: string): string {
  return value.replace(INVISIBLE_WHITESPACE, "").toLowerCase();
}

/**
 * Canonicalizes an email for storage-INDEPENDENT comparison. Starts from
 * {@link normalizeEmail}, then applies provider-specific rules so the SAME human's
 * address variants collapse to one value, while distinct people are never merged:
 *
 * - `gmail.com`/`googlemail.com`: alias domain to `gmail.com`, drop everything
 *   from the first `+` in the local part, and strip all `.` from the local part
 *   (Gmail ignores dots).
 * - Plus-addressing providers ({@link PLUS_ADDRESSING_DOMAINS}): drop the `+tag`
 *   only — dots are significant, so they're preserved.
 * - Yahoo family (`yahoo.com`/`ymail.com`/`rocketmail.com`): alias domain to
 *   `yahoo.com` only — NO `+tag` or dot stripping (Yahoo doesn't plus-address).
 * - Every other domain: whitespace-strip + lowercase only, nothing else.
 *
 * This is LOSSY — never store or send its output; use it only as a match key. Use
 * the SAME canonicalization on the write-side match key (deduping an allowlist/roster)
 * and the read-side lookup so a comparison can never silently miss on a provider alias.
 *
 * `opts.gmailStyleDomains` extends Gmail's dot + `+tag` stripping to a set of CUSTOM
 * domains known (via MX detection) to be Google-Workspace-hosted — their domain is
 * kept as-is, only the local part is canonicalized. This stays a pure, Edge-safe,
 * synchronous function: the caller resolves that set (see
 * {@link import("./mail-provider-cache").gmailStyleDomainsFor}) and passes it in.
 *
 * @param value - Raw email string.
 * @param opts.gmailStyleDomains - Custom domains to canonicalize with Gmail rules.
 * @returns The canonical match key. Values with no `@` are returned
 *   whitespace-stripped and lowercased (no local-part rules applied).
 */
export function canonicalizeEmail(value: string, opts?: { gmailStyleDomains?: ReadonlySet<string> }): string {
  const cleaned = normalizeEmail(value);

  const at = cleaned.lastIndexOf("@");
  if (at <= 0) return cleaned; // no local part to canonicalize

  let local = cleaned.slice(0, at);
  const domain = canonicalizeEmailDomain(cleaned.slice(at + 1));

  // Gmail itself, or a custom Google-Workspace domain the caller resolved, gets the
  // full treatment (drop +tag AND strip dots — Workspace domains keep their own
  // domain, not gmail.com); other plus-addressing providers drop the +tag only.
  const gmailStyle = domain === "gmail.com" || !!opts?.gmailStyleDomains?.has(domain);
  if (gmailStyle || PLUS_ADDRESSING_DOMAINS.has(domain)) {
    const plus = local.indexOf("+");
    if (plus >= 0) local = local.slice(0, plus);
    if (gmailStyle) local = local.replace(/\./g, "");
  }
  // Yahoo family (already domain-aliased) and every other domain: no local rules.
  return `${local}@${domain}`;
}

/**
 * Whether two raw emails resolve to the same inbox under {@link canonicalizeEmail}.
 * Empty/whitespace-only values never match (a blank stored value must not match a
 * blank query). Prefer this over re-inlining `a.toLowerCase() === b.toLowerCase()`
 * so every JS-side gate shares one provider-aware comparison.
 *
 * @param a - First raw email (or null/undefined).
 * @param b - Second raw email (or null/undefined).
 * @param opts.gmailStyleDomains - Custom Google-Workspace domains (see {@link canonicalizeEmail}).
 * @returns true when both are present and canonicalize to the same value.
 */
export function emailsMatch(
  a: string | null | undefined,
  b: string | null | undefined,
  opts?: { gmailStyleDomains?: ReadonlySet<string> }
): boolean {
  if (!a || !b) return false;
  const na = canonicalizeEmail(a, opts);
  const nb = canonicalizeEmail(b, opts);
  return na.length > 0 && na === nb;
}

/**
 * The set of raw domains a stored value could carry that all canonicalize to the
 * same inbox-domain as `email` — e.g. a canonical `gmail.com` address may be
 * stored as `@gmail.com` OR `@googlemail.com`. Used to build an injection-safe,
 * domain-anchored DB pre-filter (local part fully wildcarded, never user input)
 * that returns a superset of candidate rows for a `.eq()`-can't-match variant
 * lookup; callers then confirm exactly with {@link emailsMatch}.
 *
 * @param email - Raw email whose domain family to expand.
 * @returns Lowercased raw domains (canonical + all aliases pointing to it). Empty
 *   when `email` has no domain.
 */
export function emailStoredDomainVariants(email: string): string[] {
  const normalized = canonicalizeEmail(email);
  const at = normalized.lastIndexOf("@");
  if (at < 0) return [];
  const canonicalDomain = normalized.slice(at + 1);
  const variants = new Set<string>([canonicalDomain]);
  for (const [alias, target] of Object.entries(DOMAIN_ALIASES)) {
    if (target === canonicalDomain) variants.add(alias);
  }
  return [...variants];
}

/**
 * Builds the injection-safe, domain-anchored PostgREST `.or()` conditions for a
 * bounded pre-filter: one `<column>.ilike.*@<domain>` term per
 * {@link emailStoredDomainVariants} entry. The local part is always the literal `*`
 * wildcard and the domains come from the safe variant list, so the caller's raw email
 * NEVER enters the SQL filter (attacker `%`/`_` can't act as wildcards). Callers join
 * with `,`, optionally append an `attendee_id.eq.<id>` term, and confirm the exact
 * canonical match in JS (e.g. via {@link emailsMatch} or `buildEmailMatchPredicate`).
 *
 * Centralizes the derivation that the registration, roster, form-response, and chatbot
 * gates would otherwise hand-roll, so a domain-alias or anchor change lands in one spot.
 *
 * @param email - Raw email whose domain family to expand into filter terms.
 * @param column - The email column to anchor on (e.g. `"email"`, `"user_email"`). Defaults to `"email"`.
 * @returns Zero or more `.or()` condition strings; empty when `email` has no domain.
 */
export function emailDomainVariantFilters(email: string, column = "email"): string[] {
  return emailStoredDomainVariants(email).map((domain) => `${column}.ilike.*@${domain}`);
}
